<!-- Evidence progress card (evidence-progress-spec.md §Anatomía): debug-only standalone card
     family — one block per visible bracket view from the container-scoped store. Zero views,
     no store or debug mode off → nothing renders (the component auto-guards). Layout rides
     Bootstrap utilities + M3 system classes; the SCSS holds only the card chrome, the two
     fixed grids, the indent, the tabular timer, the spin/fade animations and the tone tints.
     Data chips ride the SHARED notification-badge global classes (ai-chat-cards.scss — the
     same badge vocabulary as the plan/debug cards, S10) and wrap; the header's 1fr text cell
     shrinks (min-width: 0) so the title clamp and the badges stay inside the card box at the
     280px floor. Never progress bars or percentages: only stage boundaries are known (spec
     L39). -->
@for (card of cards(); track card.key) {
  <div
    class="evidence-progress-card"
    [class.evidence-progress-card--summary]="card.mode === 'summary'"
    [class.evidence-progress-card--error]="card.errored"
  >
    <!-- a11y (spec L105): polite region announcing ONLY the stage change — never the
         chronometer. The summary line announces once when it replaces the stage list. -->
    <span class="evidence-progress-card__a11y" aria-live="polite">{{ card.a11y }}</span>

    <!-- 1. Header grid 20px | 1fr | auto: state icon, title (+ muted subtitle), clock. The
         1fr text cell carries the head-main shrink hook (min-width: 0) so the title clamp
         and the badge row stay inside the card at the 280px floor (S10). -->
    <div class="evidence-progress-card__header">
      <mat-icon
        class="mat-icon-md"
        [class]="card.headerToneClass"
        [class.evidence-progress-card__spin]="card.headerSpin"
        [matTooltip]="card.headerTooltip"
        aria-hidden="true"
      >{{ card.headerIcon }}</mat-icon>
      @if (card.mode === 'summary') {
        <div class="d-flex flex-column evidence-progress-card__head-main">
          <!-- S11: the episode name survives completion — the label (or its tool fallback)
               stays the block title above the Done line, same clamp+tooltip treatment as the
               running header. -->
          <span class="mat-font-title-sm evidence-progress-card__title" [matTooltip]="card.title">{{ card.title }}</span>
          <div class="evidence-progress-card__summary-text">
            <span class="mat-font-body-md">{{ card.summaryWhat }}</span
            ><span class="mat-font-body-md evidence-progress-card__muted">{{ card.summaryRest }}</span>
          </div>
          @if (card.kind !== null || card.repeated) {
            <div class="evidence-progress-card__chips">
              @if (card.kind; as kind) {
                <span class="notification-badge notification-badge--pending mat-font-label-sm">{{ kind }}</span>
              }
              @if (card.repeated) {
                <span class="notification-badge notification-badge--pending" [matTooltip]="card.repeatTooltip">
                  <mat-icon aria-hidden="true">repeat</mat-icon>
                </span>
              }
            </div>
          }
        </div>
        <span></span>
      } @else {
        <div class="d-flex flex-column evidence-progress-card__head-main">
          <!-- The title clamp (plan-cards-spec exception): max 2 lines, full text in the
               tooltip — with a v1.1 label this is the exact evidence text. -->
          <span class="mat-font-title-sm evidence-progress-card__title" [matTooltip]="card.title">{{ card.title }}</span>
          @if (card.subtitle; as subtitle) {
            <span class="mat-font-label-sm evidence-progress-card__muted">{{ subtitle }}</span>
          }
          @if (card.kind !== null || card.repeated) {
            <div class="evidence-progress-card__chips">
              @if (card.kind; as kind) {
                <span class="notification-badge notification-badge--pending mat-font-label-sm">{{ kind }}</span>
              }
              @if (card.repeated) {
                <span class="notification-badge notification-badge--pending" [matTooltip]="card.repeatTooltip">
                  <mat-icon aria-hidden="true">repeat</mat-icon>
                </span>
              }
            </div>
          }
        </div>
        <span class="mat-font-label-md evidence-progress-card__muted evidence-progress-card__clock">{{ card.clock }}</span>
      }
    </div>

    <!-- 2. Stage rows, grid 16px | 1fr | auto at the 28px title indent: pending
         radio_button_unchecked (dimmed), running sync spin, completed check_circle with meta,
         error error_outline. The staging row pre-shows as pending (spec L45) and the >20 s
         long-wait note stays calm (no alarm color, spec L37). -->
    @if (card.stages.length > 0 || card.pendingStaging !== null) {
      <div class="evidence-progress-card__stages">
        @for (row of card.stages; track row.stage) {
          <div
            class="evidence-progress-card__row"
            [class.evidence-progress-card__row--active]="row.active"
            [class.evidence-progress-card__row--dimmed]="row.dimmed"
          >
            <mat-icon
              class="mat-icon-sm"
              [class]="row.toneClass"
              [class.evidence-progress-card__spin]="row.spin"
              [matTooltip]="row.tooltip"
              aria-hidden="true"
            >{{ row.icon }}</mat-icon>
            <span class="evidence-progress-card__row-text">
              <span class="mat-font-body-sm" [class.evidence-progress-card__label--fade]="row.active">{{
                row.label
              }}</span>
              @if (row.layer; as layer) {
                <span class="notification-badge notification-badge--pending mat-font-label-sm">{{ layer }}</span>
              }
            </span>
            @if (row.meta; as meta) {
              <span class="mat-font-label-sm evidence-progress-card__muted evidence-progress-card__meta">{{ meta }}</span>
            } @else {
              <span></span>
            }
          </div>
        }
        @if (card.pendingStaging; as pending) {
          <div class="evidence-progress-card__row evidence-progress-card__row--dimmed">
            <mat-icon
              class="mat-icon-sm"
              [class]="pending.toneClass"
              [matTooltip]="pending.tooltip"
              aria-hidden="true"
            >{{ pending.icon }}</mat-icon>
            <span class="mat-font-body-sm">{{ pending.label }}</span>
            <span></span>
          </div>
        }
        @if (card.longNote; as note) {
          <span class="mat-font-label-sm evidence-progress-card__muted evidence-progress-card__note">{{ note }}</span>
        }
      </div>
    }
  </div>
}